import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Select,
  InputNumber,
  Button,
  Checkbox,
  Input,
  Divider,
  Row,
  Col,
  Space,
  Tag,
  Descriptions,
} from 'antd';
import { SaveOutlined, EditOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import {
  get_employee_preferences,
  update_employee_preferences,
} from '../../../utils/shift_availability_api';
import { get_employee, get_shift } from '../../../utils/api';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import { Typography } from 'antd';

const { Option } = Select;
const { Title } = Typography;
const { TextArea } = Input;

function EmployeePreferences() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [shiftList, setShiftList] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [preferencesData, setPreferencesData] = useState(null);
  const [form] = Form.useForm();

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const loadEmployeeList = async () => {
    try {
      await get_employee((res) => {
        if (res?.data?.data) {
          setEmployeeList(res.data.data);
        }
      });
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadShiftList = async () => {
    try {
      await get_shift((res) => {
        if (res?.data?.data) {
          setShiftList(res.data.data);
        }
      });
    } catch (error) {
      console.error('Error loading shifts:', error);
    }
  };

  const loadPreferences = async (employeeId) => {
    if (!employeeId) return;

    setLoading(true);
    try {
      await get_employee_preferences(employeeId, (res) => {
        if (res.data) {
          setPreferencesData(res.data);

          // Populate form with existing preferences
          form.setFieldsValue({
            employee_id: res.data.employee_id,
            preferred_shifts: res.data.preferred_shifts?.map((s) => s.id) || [],
            avoid_shifts: res.data.avoid_shifts?.map((s) => s.id) || [],
            preferred_days: res.data.preferred_days?.split(',').filter(Boolean) || [],
            avoid_days: res.data.avoid_days?.split(',').filter(Boolean) || [],
            max_shifts_per_week: res.data.max_shifts_per_week || 5,
            min_hours_between_shifts: res.data.min_hours_between_shifts || 8,
            notes: res.data.notes || '',
          });
          setEditing(false);
        } else {
          // No preferences found, start in editing mode
          setPreferencesData(null);
          form.setFieldsValue({
            employee_id: employeeId,
            max_shifts_per_week: 5,
            min_hours_between_shifts: 8,
          });
          setEditing(true);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error('Failed to load preferences');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployeeList();
    loadShiftList();
  }, []);

  const handleEmployeeChange = (employeeId) => {
    setSelectedEmployee(employeeId);
    loadPreferences(employeeId);
  };

  const handleSave = async (values) => {
    try {
      const payload = {
        employee_id: values.employee_id,
        preferred_shift_ids: values.preferred_shifts || [],
        avoid_shift_ids: values.avoid_shifts || [],
        preferred_days: values.preferred_days?.join(',') || '',
        avoid_days: values.avoid_days?.join(',') || '',
        max_shifts_per_week: values.max_shifts_per_week,
        min_hours_between_shifts: values.min_hours_between_shifts,
        notes: values.notes || '',
      };

      await update_employee_preferences(payload, (res) => {
        toast.success(res.message || 'Preferences updated successfully');
        setEditing(false);
        loadPreferences(values.employee_id);
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    }
  };

  const renderPreferencesView = () => {
    if (!preferencesData) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
          {selectedEmployee
            ? 'No preferences set for this employee. Click "Edit Preferences" to add.'
            : 'Please select an employee to view preferences.'}
        </div>
      );
    }

    return (
      <Descriptions bordered column={2} size="middle">
        <Descriptions.Item label="Employee" span={2}>
          {preferencesData.employee_name}
        </Descriptions.Item>

        <Descriptions.Item label="Preferred Shifts" span={2}>
          {preferencesData.preferred_shifts?.length > 0 ? (
            preferencesData.preferred_shifts.map((shift) => (
              <Tag color="green" key={shift.id}>
                {shift.name}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#999' }}>None</span>
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Avoid Shifts" span={2}>
          {preferencesData.avoid_shifts?.length > 0 ? (
            preferencesData.avoid_shifts.map((shift) => (
              <Tag color="red" key={shift.id}>
                {shift.name}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#999' }}>None</span>
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Preferred Days">
          {preferencesData.preferred_days || <span style={{ color: '#999' }}>None</span>}
        </Descriptions.Item>

        <Descriptions.Item label="Avoid Days">
          {preferencesData.avoid_days || <span style={{ color: '#999' }}>None</span>}
        </Descriptions.Item>

        <Descriptions.Item label="Max Shifts Per Week">
          {preferencesData.max_shifts_per_week || 5}
        </Descriptions.Item>

        <Descriptions.Item label="Min Hours Between Shifts">
          {preferencesData.min_hours_between_shifts || 8} hours
        </Descriptions.Item>

        <Descriptions.Item label="Notes" span={2}>
          {preferencesData.notes || <span style={{ color: '#999' }}>None</span>}
        </Descriptions.Item>
      </Descriptions>
    );
  };

  const renderPreferencesForm = () => {
    return (
      <Form form={form} onFinish={handleSave} layout="vertical" style={{ marginTop: 20 }}>
        <Form.Item name="employee_id" hidden>
          <Input />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="preferred_shifts"
              label="Preferred Shifts"
              tooltip="Select shifts you prefer to work"
            >
              <Select
                mode="multiple"
                placeholder="Select preferred shifts"
                optionFilterProp="children"
              >
                {shiftList.map((shift) => (
                  <Option key={shift.shift_id} value={shift.shift_id}>
                    {shift.shift}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="avoid_shifts"
              label="Avoid Shifts"
              tooltip="Select shifts you want to avoid"
            >
              <Select
                mode="multiple"
                placeholder="Select shifts to avoid"
                optionFilterProp="children"
              >
                {shiftList.map((shift) => (
                  <Option key={shift.shift_id} value={shift.shift_id}>
                    {shift.shift}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="preferred_days"
              label="Preferred Days of Week"
              tooltip="Select days you prefer to work"
            >
              <Checkbox.Group options={daysOfWeek} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="avoid_days"
              label="Days to Avoid"
              tooltip="Select days you want to avoid"
            >
              <Checkbox.Group options={daysOfWeek} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="max_shifts_per_week"
              label="Maximum Shifts Per Week"
              tooltip="Maximum number of shifts you want per week"
            >
              <InputNumber min={1} max={7} style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="min_hours_between_shifts"
              label="Minimum Rest Hours Between Shifts"
              tooltip="Minimum hours of rest required between shifts"
            >
              <InputNumber min={8} max={24} style={{ width: '100%' }} addonAfter="hours" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="notes"
          label="Additional Notes"
          tooltip="Any additional preference information"
        >
          <TextArea rows={3} placeholder="Enter any additional preference notes..." />
        </Form.Item>

        <Divider />

        <Form.Item style={{ marginBottom: 0 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setEditing(false);
                if (preferencesData) {
                  loadPreferences(selectedEmployee);
                }
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              style={{
                backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                borderColor: isDark ? '#0F172A' : defaultColors.primary,
              }}
            >
              Save Preferences
            </Button>
          </Space>
        </Form.Item>
      </Form>
    );
  };

  return (
    <Card
      className="m-2.5 border border-gray-300 shadow-md"
      title={
        <div className="flex items-center gap-2">
          <Icons.Settings className="text-blue-600" size={24} />
          <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
            {t('Employee Shift Preferences')}
          </Title>
        </div>
      }
      extra={
        <Space>
          <Select
            placeholder="Select employee"
            style={{ width: 250 }}
            showSearch
            value={selectedEmployee}
            onChange={handleEmployeeChange}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {employeeList?.map((emp) => (
              <Option key={emp.employee_id} value={emp.employee_id}>
                {emp.employee_name || emp.username}
              </Option>
            ))}
          </Select>

          {selectedEmployee && !editing && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setEditing(true)}
              style={{
                backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                borderColor: isDark ? '#0F172A' : defaultColors.primary,
              }}
            >
              Edit Preferences
            </Button>
          )}
        </Space>
      }
      loading={loading}
    >
      {editing ? renderPreferencesForm() : renderPreferencesView()}
    </Card>
  );
}

export default EmployeePreferences;
